import React from "react";
import { Link } from "react-router-dom";
import "./StepProgressBar.css";

/**
 * Step progress — same visual language as Lighthouse StageTabBar
 * (Fragment Mono uppercase tabs + accent underline). Shared by member
 * onboarding and the clinic runner.
 *
 * Presentational only: callers own step data, availability, and navigation.
 * Labels always stay visible; the strip wraps when steps cannot fit.
 */
export const StepProgressBar = ({
  steps,
  currentStepId,
  availableStepIds,
  getStepHref,
  onStepClick,
}) => {
  if (!steps || steps.length === 0) return null;

  const currentStepIndex = Math.max(
    0,
    steps.findIndex((step) => step.id === currentStepId),
  );

  const isAvailableStep = (step, idx) => {
    if (availableStepIds) return availableStepIds.includes(step.id);
    return idx <= currentStepIndex;
  };

  return (
    <nav
      className="onyx-stage-tabs onboarding-progress"
      data-testid="onboarding-progress"
      aria-label="Onboarding steps"
    >
      {steps.map((step, index) => {
        const isCurrent = index === currentStepIndex;
        const isPast = index < currentStepIndex;
        const isAvailable = isAvailableStep(step, index);
        const href = isAvailable && getStepHref ? getStepHref(step) : null;
        const handleClick =
          isAvailable && onStepClick ? () => onStepClick(step) : null;

        const className = [
          "onyx-stage-tabs__tab",
          "onboarding-progress__step",
          isCurrent ? "onyx-stage-tabs__tab--active onboarding-progress__step--current" : "",
          isPast ? "onboarding-progress__step--completed" : "",
          !isAvailable ? "onboarding-progress__step--disabled" : "",
        ]
          .filter(Boolean)
          .join(" ");

        const label = (
          <span className="onyx-stage-tabs__label onboarding-progress__step-label">
            {step.label}
          </span>
        );

        const check = isPast ? (
          <span className="onboarding-progress__check" aria-hidden="true">
            ✓
          </span>
        ) : null;

        const number = !isPast ? (
          <span className="onboarding-progress__step-number" aria-hidden="true">
            {index + 1}
          </span>
        ) : null;

        const inner = (
          <>
            {number}
            {check}
            {label}
          </>
        );

        const stepAriaLabel = `${index + 1}. ${step.label}${
          isPast ? ", completed" : isCurrent ? ", current" : ""
        }`;

        if (handleClick) {
          return (
            <button
              key={step.id}
              type="button"
              onClick={handleClick}
              className={`${className} onboarding-progress__step-circle onboarding-progress__step-circle--button onboarding-progress__step-label--button`}
              aria-current={isCurrent ? "step" : undefined}
              aria-label={stepAriaLabel}
              title={step.label}
            >
              {inner}
            </button>
          );
        }

        if (href) {
          return (
            <Link
              key={step.id}
              to={href}
              className={`${className} onboarding-progress__step-circle`}
              aria-current={isCurrent ? "step" : undefined}
              aria-label={stepAriaLabel}
              title={step.label}
            >
              {inner}
            </Link>
          );
        }

        return (
          <div
            key={step.id}
            className={`${className} onboarding-progress__step-circle`}
            aria-current={isCurrent ? "step" : undefined}
            aria-label={stepAriaLabel}
            title={step.label}
          >
            {inner}
          </div>
        );
      })}
    </nav>
  );
};

export default StepProgressBar;
